import React, {useCallback, useEffect, useRef, useState} from 'react';
import IdleTimer from 'react-idle-timer';
import classNames from 'classnames';
import {clearAllLocalStorage, getLocalData, removeLocalData} from '../../../../services/StorageServices';
import NavbarLeft from '../nav-left/NavbarLeftContainer';
import NavbarTop from '../nav-top/NavbarTopContainer';
import {useOnScreen} from '../../../../helpers/checkUseOnScreen';
import {logoutPhone} from '../../../../services/api/apiPhoneConnect';
import parseToken from '../../../../helpers/parseToken';
import ContextContainer from '../../../shared/components/ContextContainer';

const MOBILE_BREAKPOINT = 992;
const DEFAULT_IDLE_TIME = 30 * 60; // 30 minutes

function DefaultLayout(props) {
    const decodeToken = parseToken();
    const ref = useRef();
    const idleTimer = useRef(null);
    const timeoutRef = useRef(null);

    const [isCollapsed, setIsCollapsed] = useState(() =>
        getLocalData('navigationMenuCollapse', true)
    );
    const [width, setWidth] = useState(window.innerWidth);
    const [isShowComponent, setIsShowComponent] = useState(true);
    const isVisible = useOnScreen(ref);

    const clearSessionTimeout = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);

    const handleLogout = useCallback(async () => {
        try {
            removeLocalData('roles');

            if (decodeToken?.sub) {
                await logoutPhone({vsaUser: decodeToken.sub});
            }

            clearAllLocalStorage();
        } catch (e) {
            console.error('Logout error:', e.message);
        }
    }, [decodeToken]);

    const handleClearData = useCallback(async () => {
        await handleLogout();
        await removeLocalData('accessToken');
        window.location.href = 'https://admin.callstack.vn:2008/passportv3/logout?service=https://admin.callstack.vn';
    }, [handleLogout]);

    const handleToggle = useCallback((e) => {
        e?.preventDefault();
        setIsCollapsed(prev => !prev);
    }, []);

    const handleMenuItemClick = useCallback(() => {
        if (width <= MOBILE_BREAKPOINT) {
            setIsCollapsed(true);
        }
    }, [width]);

    const onActive = useCallback(() => {
        if (!isVisible) {
            clearSessionTimeout();
        }
    }, [isVisible, clearSessionTimeout]);

    const onIdle = useCallback(() => {
        const time = getLocalData('timeScreen') ?? DEFAULT_IDLE_TIME;

        if (time === 0 || isVisible) return;

        const delay = time * 1000;
        timeoutRef.current = setTimeout(() => {
            clearSessionTimeout();
            handleClearData();
        }, delay);
    }, [isVisible, clearSessionTimeout, handleClearData]);

    // Window focus/blur handlers
    useEffect(() => {
        const handleFocus = () => setIsShowComponent(true);
        const handleBlur = () => {
            setIsShowComponent(false);
            clearSessionTimeout();
        };

        window.addEventListener('focus', handleFocus);
        window.addEventListener('blur', handleBlur);

        return () => {
            window.removeEventListener('focus', handleFocus);
            window.removeEventListener('blur', handleBlur);
        };
    }, [clearSessionTimeout]);

    // Resize handler
    useEffect(() => {
        const handleResize = () => setWidth(window.innerWidth);

        window.addEventListener('resize', handleResize);

        if (width <= MOBILE_BREAKPOINT) {
            setIsCollapsed(true);
        }

        return () => window.removeEventListener('resize', handleResize);
    }, [width]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => clearSessionTimeout();
    }, [clearSessionTimeout]);

    return (
        <div className="Layout">
            <div className={classNames('DashboardContainer', {isCollapsed})}>
                {isShowComponent && <div className="showActive" ref={ref}/>}

                <IdleTimer
                    ref={idleTimer}
                    onActive={onActive}
                    onIdle={onIdle}
                    debounce={250}
                    timeout={5000}
                />

                <NavbarLeft
                    isCollapsed={isCollapsed}
                    onClickToggle={handleToggle}
                    onClickMenuItem={handleMenuItemClick}
                />

                <div className="DashboardInner">
                    <div className="MainContent">
                        <div className="MainContentInner">
                            <ContextContainer {...props}>
                                {props.children({menuCollapsed: isCollapsed})}
                            </ContextContainer>
                        </div>
                    </div>
                    <NavbarTop isCollapsed={isCollapsed} onClick={handleToggle}/>
                </div>
            </div>
        </div>
    );
}

export default React.memo(DefaultLayout);